import axios from 'axios';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'https://qeasee.codepackergrf.com/api',
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

const deleteWithFallback = async (url) => {
    try {
        return await api.delete(url);
    } catch (error) {
        const status = error?.response?.status;
        if (status === 404 || status === 405) {
            return api.post(`${url}/delete`, {});
        }
        throw error;
    }
};

const deleteWithFallbackDokter = async (url) => {
    try {
        return await api.delete(url);
    } catch (error) {
        const status = error?.response?.status;
        if (status === 404 || status === 405) {
            return api.post(`${url}/delete`, {});
        }
        throw error;
    }
};

api.interceptors.request.use(
    (config) => {
        const rawToken = localStorage.getItem('token');
        const fallbackToken = (() => {
            try {
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                return user?.token || null;
            } catch {
                return null;
            }
        })();
        const token = (rawToken || fallbackToken || '').replace(/^"|"$/g, '').trim();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // FormData must not use application/json or a manual multipart boundary — browser sets multipart correctly.
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        return config;
    },
    (error) => Promise.reject(error)
);

/**
 * Per-tenant API (multi-tenant). Use slug from useParams(), e.g. axios.get(`/api/${slug}/pasien`).
 */
export function tenantApi(slug) {
    if (!slug || typeof slug !== 'string') {
        throw new Error('tenantApi(slug): slug is required');
    }
    const p = `/${encodeURIComponent(slug)}`;
    return {
        poli: {
            getAll: (params) => api.get(`${p}/poli`, { params }),
            getById: (id) => api.get(`${p}/poli/${id}`),
            create: (data) => api.post(`${p}/poli`, data),
            update: (id, data) => api.put(`${p}/poli/${id}`, data),
            delete: (id) => deleteWithFallback(`${p}/poli/${id}`),
        },
        dokter: {
            getAll: (params) => api.get(`${p}/dokter`, { params }),
            getById: (id) => api.get(`${p}/dokter/${id}`),
            create: (data) => api.post(`${p}/dokter`, data),
            createWithForm: (formData) => api.post(`${p}/dokter`, formData),
            update: (id, data) => api.put(`${p}/dokter/${id}`, data),
            updateWithForm: (id, formData) => api.post(`${p}/dokter/${id}`, formData),
            delete: (id) => deleteWithFallbackDokter(`${p}/dokter/${id}`),
        },
        antrian: {
            getAll: (params) => api.get(`${p}/antrian`, { params }),
            getById: (id) => api.get(`${p}/antrian/${id}`),
            create: (data) => api.post(`${p}/antrian`, data),
            update: (id, data) => api.put(`${p}/antrian/${id}`, data),
            delete: (id) => api.delete(`${p}/antrian/${id}`),
        },
        notificationFeed: () => api.get(`${p}/admin/notification-feed`),
        pasien: {
            getAll: (params) => api.get(`${p}/pasien`, { params }),
            getById: (id) => api.get(`${p}/pasien/${id}`),
            create: (data) => api.post(`${p}/pasien`, data),
            update: (id, data) => api.put(`${p}/pasien/${id}`, data),
            delete: (id) => deleteWithFallback(`${p}/pasien/${id}`),
        },
    };
}

/** Legacy top-level poli/dokter/antrian: pass id_klinik (or rely on auth) — never returns all clinics. */
const marketingKlinikId = process.env.REACT_APP_MARKETING_KLINIK_ID;

export const poliService = {
    getAll: (params) =>
        api.get('/poli', { params: { id_klinik: marketingKlinikId, ...params } }),
    getById: (id) => api.get(`/poli/${id}`),
    create: (data) => api.post('/poli', data),
    update: (id, data) => api.put(`/poli/${id}`, data),
    delete: (id) => deleteWithFallback(`/poli/${id}`),
};

export const dokterService = {
    getAll: (params) =>
        api.get('/dokter', { params: { id_klinik: marketingKlinikId, ...params } }),
    getById: (id) => api.get(`/dokter/${id}`),
    create: (data) => api.post('/dokter', data),
    createWithForm: (formData) => api.post('/dokter', formData),
    update: (id, data) => api.put(`/dokter/${id}`, data),
    updateWithForm: (id, formData) => api.post(`/dokter/${id}`, formData),
    delete: (id) => deleteWithFallbackDokter(`/dokter/${id}`),
};

export const antrianService = {
    getAll: (params) =>
        api.get('/antrian', { params: { id_klinik: marketingKlinikId, ...params } }),
    getById: (id) => api.get(`/antrian/${id}`),
    create: (data) => api.post('/antrian', data),
    update: (id, data) => api.put(`/antrian/${id}`, data),
    delete: (id) => api.delete(`/antrian/${id}`),
};

export const paymentService = {
    getSnapToken: (data) => api.post('/payment/snap-token', data),
};

export const authService = {
    logout: () => api.post('/logout'),
};

export const clinicService = {
    getBySlug: (slug) => api.get(`/clinic/${encodeURIComponent(slug)}`),
    /** Legacy (no slug in URL); prefer getSettingsForSlug when clinic admin uses /:slug/clinic-admin/settings */
    getSettings: () => api.get('/clinic/settings'),
    updateSettings: (formData) => api.post('/clinic/settings', formData),
    getDashboardStatsForSlug: (slug) => api.get(`/${encodeURIComponent(slug)}/admin/dashboard-stats`),
    getSettingsForSlug: (slug) => api.get(`/${encodeURIComponent(slug)}/clinic/settings`),
    updateSettingsForSlug: (slug, formData) => api.post(`/${encodeURIComponent(slug)}/clinic/settings`, formData),
};

/** Tenant Kritik & Saran: `payload` must include `slug` (same as URL segment) plus name, email, message. */
export const clinicFeedbackService = {
    submit: (slug, payload) =>
        api.post(`/${encodeURIComponent(slug)}/feedback`, payload),
};

export const contactService = {
    submit: (payload) => api.post('/contact-messages', payload),
};

export const doctorDashboardService = {
    getQueues: (slug, params) =>
        api.get(`/${encodeURIComponent(slug)}/dashboard/doctor/antrian`, { params }),
    getPatients: (slug, params) => api.get(`/${encodeURIComponent(slug)}/patients`, { params }),
};

export const adminService = {
    getDashboardStats: () => api.get('/admin/dashboard-stats'),
    getNotificationFeed: () => api.get('/admin/notification-feed'),
    updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
    deleteUser: (id) => api.delete(`/admin/users/${id}`),
    updateSubscription: (id, data) => api.put(`/admin/subscriptions/${id}`, data),
};

export default api;
